<div class="mb-6 flex flex-wrap items-center justify-between gap-4">
  <div>
    <h1 class="!mt-0 !mb-2">Consumables</h1>
    <p class="!mt-0 text-muted-color">
      Manage standard items such as bolts, resin, and tooling that can be added
      to quote lines.
    </p>
  </div>
  <p-button label="New Consumable" icon="pi pi-plus" (onClick)="openDrawer()" />
</div>

<div class="flex flex-wrap gap-3 items-end justify-between mb-4">
  <p-iconfield>
    <p-inputicon styleClass="pi pi-search" />
    <input
      pInputText
      [formControl]="searchQuery"
      placeholder="Search consumables…"
      class="w-64"
    />
  </p-iconfield>
  <div class="flex items-center gap-2">
    <p-checkbox
      [formControl]="includeArchived"
      [binary]="true"
      inputId="includeArchivedConsumables"
    />
    <label for="includeArchivedConsumables" class="text-sm font-medium">
      Include archived consumables
    </label>
  </div>
</div>

@if (saveError && !isDrawerVisible) {
  <p-message severity="error" styleClass="w-full mb-4">{{
    saveError
  }}</p-message>
}

@let rows = tableConsumables$ | async;
@let currencyCode = currencyCode$ | async;
@if (rows && rows.length > 0) {
  <div
    class="rounded-xl border border-surface-switch-200 shadow-sm overflow-x-auto"
  >
    <div
      class="hidden md:grid rounded-t-xl bg-surface-switch-50 items-center gap-4 px-4 py-3 text-sm font-bold text-surface-switch-600 uppercase border-b border-surface-switch-200"
      style="
        grid-template-columns:
          minmax(0, 1.5fr) minmax(0, 2fr) minmax(9rem, 1fr)
          8rem;
      "
    >
      <div>Name</div>
      <div>Description</div>
      <div>Cost</div>
      <div class="text-center">Actions</div>
    </div>
    @for (row of rows; track row.id) {
      <div
        class="grid items-center gap-4 border-b border-surface-switch-200 px-4 py-3 last:border-b-0 bg-surface-switch-0"
        style="
          grid-template-columns:
            minmax(0, 1.5fr) minmax(0, 2fr) minmax(9rem, 1fr)
            8rem;
        "
      >
        <div class="min-w-0 font-semibold">
          {{ row.name }}
          @if (row.is_archived) {
            <p-chip label="Archived" styleClass="ml-2 text-xs" />
          }
        </div>
        <div
          class="min-w-0 text-sm text-muted-color line-clamp-2"
          [title]="row.description"
        >
          {{ row.description || "—" }}
        </div>
        <div>
          {{ row.cost | currency: currencyCode ?? "" : "symbol" : "1.2-2" }}
          <span class="text-muted-color">/ {{ unitLabel(row.unit) }}</span>
        </div>
        <div class="flex justify-center gap-1">
          <p-button
            icon="pi pi-pencil"
            severity="secondary"
            size="small"
            [text]="true"
            [rounded]="true"
            pTooltip="Edit"
            tooltipPosition="top"
            (onClick)="openDrawer(row)"
          />
          @if (!row.is_archived) {
            <p-button
              icon="pi pi-box"
              severity="secondary"
              size="small"
              [text]="true"
              [rounded]="true"
              pTooltip="Archive"
              tooltipPosition="top"
              (onClick)="confirmArchive(row, true)"
            />
          } @else {
            <p-button
              icon="pi pi-refresh"
              severity="secondary"
              size="small"
              [text]="true"
              [rounded]="true"
              pTooltip="Restore"
              tooltipPosition="top"
              (onClick)="confirmArchive(row, false)"
            />
          }
        </div>
      </div>
    }
  </div>
} @else {
  <div class="text-center text-muted-color">
    <p>No consumables</p>
  </div>
}

<shared-confirm-dialog />

<p-drawer
  [(visible)]="isDrawerVisible"
  [header]="consumableInDrawer ? 'Edit Consumable' : 'Add Consumable'"
  position="right"
  styleClass="!w-full md:!w-[28rem]"
  (onHide)="closeDrawer()"
>
  @if (saveError) {
    <p-message severity="error" styleClass="w-full mb-4">{{
      saveError
    }}</p-message>
  }
  <form
    [formGroup]="consumableForm"
    class="flex flex-col gap-5"
    (ngSubmit)="save()"
  >
    @let drawerCurrency = currencyCode$ | async;
    <div class="pt-1">
      <p-floatlabel variant="on">
        <input
          pInputText
          formControlName="name"
          class="w-full"
          [invalid]="showError('name')"
        />
        <label>Name</label>
      </p-floatlabel>
      @if (showError("name")) {
        <small class="mt-1 block text-red-500">
          {{
            consumableForm.controls.name.hasError("maxlength")
              ? "Name must be 255 characters or fewer."
              : "Name is required."
          }}
        </small>
      }
    </div>
    <div class="pt-1">
      <p-floatlabel variant="on">
        <p-inputNumber
          formControlName="cost"
          mode="currency"
          [currency]="drawerCurrency || 'GBP'"
          locale="en-GB"
          [min]="0"
          [minFractionDigits]="2"
          [maxFractionDigits]="2"
          styleClass="w-full"
          inputStyleClass="w-full"
          [invalid]="showError('cost')"
        />
        <label>Cost</label>
      </p-floatlabel>
      @if (showError("cost")) {
        <small class="mt-1 block text-red-500">Cost is required.</small>
      }
    </div>
    <div>
      <label class="mb-2 block text-sm font-medium" for="consumableUnit">
        Unit
      </label>
      <p-select
        inputId="consumableUnit"
        [options]="unitOptions"
        formControlName="unit"
        class="w-full"
      >
        <ng-template #selectedItem let-unit>{{ unitLabel(unit) }}</ng-template>
        <ng-template #item let-unit>{{ unitLabel(unit) }}</ng-template>
      </p-select>
    </div>
    <div>
      <label class="mb-2 block text-sm font-medium" for="consumableDescription">
        Description
      </label>
      <textarea
        pTextarea
        id="consumableDescription"
        formControlName="description"
        class="w-full"
        rows="3"
      ></textarea>
    </div>
    <div class="flex justify-end gap-2">
      <p-button
        type="button"
        label="Cancel"
        severity="secondary"
        (onClick)="closeDrawer()"
      />
      <p-button type="submit" label="Save" icon="pi pi-check" />
    </div>
  </form>
</p-drawer>
